import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BILL_STATUS_DEFS, BILL_STATUS_MAP, computeTotals, round2, type BillStatus } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money, plural, WORDS } from '../../lib/format';
import { useTenantProfile } from '../../lib/settings';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import { FxRateField } from '../../components/FxRateField';
import { currencySymbol, fxMoney, isForeignCurrency, toBase } from '../../lib/currency';
import {
  exportDay,
  exportNotes,
  exportNum,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface BillLine {
  id: string;
  accountId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  account: { id: string; code: string; name: string };
}

interface BillPayment {
  id: string;
  number: string;
  amount: string;
  paidAt: string;
  method: string | null;
  reference: string | null;
  cashAccount: { id: string; name: string };
}

interface BillRow {
  id: string;
  number: string;
  supplierRef: string | null;
  status: BillStatus;
  issuedAt: string;
  dueAt: string;
  subtotal: string;
  discount: string;
  taxRate: string;
  taxAmount: string;
  total: string;
  paidAmount: string;
  notes: string | null;
  supplier: { id: string; name: string };
  lines?: BillLine[];
  payments?: BillPayment[];
}

interface BillPage {
  items: BillRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  totals: { total: number; paid: number; outstanding: number };
}

interface Option {
  id: string;
  code?: string;
  name: string;
}

/** أعمدة الجدول بمفاتيحها — المفتاح نفسه مفتاح الفلتر في رأس العمود. */
const BILL_COLUMNS = [
  { key: 'number', label: 'الرقم', kind: 'code' },
  { key: 'supplier', label: 'المورد' },
  { key: 'supplierRef', label: 'مرجع المورد' },
  { key: 'issuedAt', label: 'التاريخ' },
  { key: 'dueAt', label: 'الاستحقاق' },
  { key: 'total', label: 'الإجمالي' },
  { key: 'outstanding', label: 'المتبقي' },
  { key: 'status', label: 'الحالة', wrap: false },
] as const;

/**
 * أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في bills.service.ts.
 * «المتبقي» محسوبٌ من الإجمالي والمسدَّد لا حقلٌ مخزَّن، فلا فلتر له.
 */
const BILL_FILTERS: FilterDef[] = [
  { key: 'number', label: 'الرقم', kind: 'text', sortable: true },
  { key: 'supplier', label: 'المورد', kind: 'text' },
  { key: 'supplierRef', label: 'مرجع المورد', kind: 'text' },
  { key: 'issuedAt', label: 'التاريخ', kind: 'date', sortable: true },
  { key: 'dueAt', label: 'الاستحقاق', kind: 'date', sortable: true },
  { key: 'total', label: 'الإجمالي', kind: 'number', sortable: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: BILL_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
];

export function BillsList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const status = (state.status ?? '') as BillStatus | '';
  const canEdit = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['bills', query],
    queryFn: () => api<BillPage>(`/finance/bills${qs(query)}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['bills'] });
    void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] });
  };

  /*
   * التصدير: كل فواتير الموردين المطابقة للبحث والحالة والمدى والفلاتر، لا
   * الصفحة المعروضة وحدها. والإجمالي على كل الصفوف كبطاقات الصفحة.
   */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<BillRow>('/finance/bills', query, 100);
    return {
      title: 'فواتير الموردين',
      sheets: [
        {
          title: 'فواتير الموردين',
          period: exportPeriod(state.from, state.to),
          sections: [
            {
              columns: [
                { key: 'number', label: 'الرقم', kind: 'code' },
                { key: 'supplier', label: 'المورد' },
                { key: 'supplierRef', label: 'مرجع المورد' },
                { key: 'issuedAt', label: 'التاريخ', kind: 'date' },
                { key: 'dueAt', label: 'الاستحقاق', kind: 'date' },
                { key: 'total', label: 'الإجمالي', kind: 'money', total: true },
                { key: 'paidAmount', label: 'المسدَّد', kind: 'money', total: true },
                { key: 'outstanding', label: 'المتبقي', kind: 'money', total: true },
                { key: 'status', label: 'الحالة', wrap: false },
              ],
              rows: items.map((b) => ({
                number: b.number,
                supplier: b.supplier.name,
                supplierRef: b.supplierRef,
                issuedAt: exportDay(b.issuedAt),
                dueAt: exportDay(b.dueAt),
                total: exportNum(b.total),
                paidAmount: exportNum(b.paidAmount),
                outstanding: round2(Number(b.total) - Number(b.paidAmount)),
                status: BILL_STATUS_MAP[b.status]?.label ?? b.status,
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: [
              status && `الحالة: ${BILL_STATUS_MAP[status]?.label ?? status}`,
              ...describeFilters(BILL_FILTERS, filters),
            ],
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="فواتير الموردين"
        subtitle="ما عليك للموردين ومواعيد استحقاقه"
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              فاتورة مورد
            </button>
          )
        }
      />

      <div className="stats">
        <Stat label="عدد الفواتير" value={list.data ? list.data.total : '—'} />
        <Stat
          label="الإجمالي"
          value={<span className="num">{list.data ? money(list.data.totals.total) : '—'}</span>}
        />
        <Stat
          label="المسدَّد"
          value={<span className="num pos">{list.data ? money(list.data.totals.paid) : '—'}</span>}
        />
        <Stat
          label="المتبقي"
          value={
            <span className="num neg">
              {list.data ? money(list.data.totals.outstanding) : '—'}
            </span>
          }
        />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالرقم أو مرجع المورد"
        />

        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!status ? ' btn-primary' : ''}`}
            onClick={() => patch({ status: undefined })}
          >
            الكل
          </button>
          {BILL_STATUS_DEFS.map((s) => (
            <button
              key={s.key}
              className={`btn btn-sm${status === s.key ? ' btn-primary' : ''}`}
              onClick={() => patch({ status: s.key })}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* مدى تاريخ الإصدار — والبطاقات فوق تتبعه، فإجمالياتها من الخادم على الشرط نفسه. */}
        <DateRangeFilter
          from={state.from}
          to={state.to}
          onChange={(f, t) => patch({ from: f, to: t })}
        />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={BILL_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={BILL_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={BILL_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/bills/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {BILL_COLUMNS.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={8} />}

            {list.data && (
              <tbody>
                {list.data.items.map((b) => {
                  const outstanding = round2(Number(b.total) - Number(b.paidAmount));
                  const overdue =
                    outstanding > 0 &&
                    b.status !== 'CANCELLED' &&
                    new Date(b.dueAt) < new Date();
                  return (
                    <tr key={b.id} className="clickable" onClick={() => setOpenId(b.id)}>
                      <td className="num cell-mute">{b.number}</td>
                      <td className="cell-strong">{b.supplier.name}</td>
                      <td className="cell-mute">
                        {/* مرجعُ المورد نصٌّ حرّ: رقمُ فاتورته اللاتينيّ أو وصفٌ عربيّ — اتجاهُه من أوّل حرف. */}
                        <bdi className="nowrap">{b.supplierRef ?? '—'}</bdi>
                      </td>
                      <td className="num cell-mute">{date(b.issuedAt)}</td>
                      <td className={`num ${overdue ? 'neg' : 'cell-mute'}`}>{date(b.dueAt)}</td>
                      <td className="num">{money(b.total)}</td>
                      <td className="num cell-strong">{outstanding ? money(outstanding) : '—'}</td>
                      <td>
                        <Badge kind={BILL_STATUS_MAP[b.status].kind}>
                          {BILL_STATUS_MAP[b.status].label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا فواتير موردين"
            text="سجّل فاتورة مورد لتتابع ما عليك ومواعيد استحقاقه، وتُرحَّل تلقائيًا إلى الدفاتر عند استلامها."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setCreating(true)}>
                  فاتورة مورد
                </button>
              )
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {creating && (
        <BillForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}

      {openId && (
        <BillDetail id={openId} onClose={() => setOpenId(null)} onChanged={refresh} />
      )}
    </div>
  );
}

/* ==========================================================================
   إنشاء فاتورة
   ========================================================================== */

interface DraftLine {
  key: string;
  accountId: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

let lineKey = 0;
const emptyLine = (): DraftLine => ({
  key: `l${lineKey++}`,
  accountId: '',
  description: '',
  quantity: '1',
  unitPrice: '',
});

function BillForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const profile = useTenantProfile();
  const today = new Date().toISOString().slice(0, 10);

  const [supplierId, setSupplierId] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [issuedAt, setIssuedAt] = useState(today);
  const [dueAt, setDueAt] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [discount, setDiscount] = useState('0');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);

  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () =>
      api<{ items: Array<Option & { paymentTermDays: number }> }>(
        '/finance/suppliers?pageSize=100&isActive=true',
      ),
  });
  const accounts = useQuery({
    queryKey: ['ledger', 'postable'],
    queryFn: () => api<Option[]>('/accounting/accounts/postable'),
  });

  const effectiveTax =
    taxRate === '' ? (profile.data?.settings.taxRate ?? 14) : Number(taxRate) || 0;

  const totals = computeTotals(
    lines
      .filter((l) => l.accountId && l.description.trim())
      .map((l) => ({ quantity: Number(l.quantity) || 0, unitPrice: Number(l.unitPrice) || 0 })),
    { headerDiscount: Number(discount) || 0, taxRate: effectiveTax },
  );

  const save = useMutation({
    mutationFn: () =>
      api('/finance/bills', {
        method: 'POST',
        body: {
          supplierId,
          supplierRef: supplierRef || undefined,
          issuedAt,
          dueAt: dueAt || undefined,
          taxRate: taxRate === '' ? undefined : Number(taxRate),
          discount: Number(discount) || 0,
          notes: notes || undefined,
          lines: lines
            .filter((l) => l.accountId && l.description.trim())
            .map((l) => ({
              accountId: l.accountId,
              description: l.description.trim(),
              quantity: Number(l.quantity) || 1,
              unitPrice: Number(l.unitPrice) || 0,
            })),
        },
      }),
    onSuccess: onSaved,
  });

  const patch = (key: string, changes: Partial<DraftLine>) =>
    setLines((list) => list.map((l) => (l.key === key ? { ...l, ...changes } : l)));

  const validLines = lines.filter((l) => l.accountId && l.description.trim());
  const selectedSupplier = suppliers.data?.items.find((s) => s.id === supplierId);

  return (
    <Modal
      title="فاتورة مورد جديدة"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!supplierId || validLines.length === 0 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ كمسودة'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="b-supplier">
            المورد
          </label>
          <select
            id="b-supplier"
            className="select"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">اختر موردًا</option>
            {suppliers.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {selectedSupplier && (
            <span className="tiny mute">
              مهلة السداد المتفق عليها {plural(selectedSupplier.paymentTermDays, WORDS.day)}.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="b-ref">
            رقم الفاتورة لدى المورد
          </label>
          <input
            id="b-ref"
            className="input"
            value={supplierRef}
            onChange={(e) => setSupplierRef(e.target.value)}
            dir="ltr"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="b-issued">
            تاريخ الفاتورة
          </label>
          <input
            id="b-issued"
            className="input"
            type="date"
            value={issuedAt}
            onChange={(e) => setIssuedAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="b-due">
            تاريخ الاستحقاق
          </label>
          <input
            id="b-due"
            className="input"
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
          <span className="tiny mute">اتركه فارغًا ليُحسب من مهلة المورد.</span>
        </div>
      </div>

      <h4 className="section-title">البنود</h4>

      <div className="table-wrap">
        <table className="table lines-table">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>الحساب</th>
              <th>البيان</th>
              <th style={{ width: 90 }}>الكمية</th>
              <th style={{ width: 120 }}>السعر</th>
              <th style={{ width: 120 }}>الإجمالي</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={line.key}>
                <td>
                  <select
                    className="select"
                    value={line.accountId}
                    aria-label={`حساب البند ${index + 1}`}
                    onChange={(e) => patch(line.key, { accountId: e.target.value })}
                  >
                    <option value="">اختر حسابًا</option>
                    {accounts.data?.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="input"
                    value={line.description}
                    aria-label={`بيان البند ${index + 1}`}
                    onChange={(e) => patch(line.key, { description: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    aria-label={`كمية البند ${index + 1}`}
                    value={line.quantity}
                    onChange={(e) => patch(line.key, { quantity: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    aria-label={`سعر البند ${index + 1}`}
                    value={line.unitPrice}
                    onChange={(e) => patch(line.key, { unitPrice: e.target.value })}
                  />
                </td>
                <td className="num strong">
                  {money((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0))}
                </td>
                <td className="end">
                  <button
                    className="btn btn-sm btn-icon btn-ghost"
                    type="button"
                    aria-label={`حذف البند ${index + 1}`}
                    disabled={lines.length === 1}
                    onClick={() => setLines((l) => l.filter((r) => r.key !== line.key))}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        className="btn btn-sm mt-2"
        type="button"
        onClick={() => setLines((l) => [...l, emptyLine()])}
      >
        <Icon name="plus" size={14} />
        إضافة بند
      </button>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span>المجموع</span>
          <span className="num">{money(totals.subtotal)}</span>
        </div>
        <div className="totals-row">
          <label htmlFor="b-discount">خصم</label>
          <input
            id="b-discount"
            className="input num"
            type="number"
            min="0"
            step="any"
            style={{ width: 120 }}
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
        </div>
        <div className="totals-row">
          <label htmlFor="b-tax">
            الضريبة
            <span className="tiny mute"> (نسبة مئوية)</span>
          </label>
          <div className="row" style={{ gap: 6 }}>
            <input
              id="b-tax"
              className="input num"
              type="number"
              min="0"
              max="100"
              step="any"
              style={{ width: 70 }}
              value={taxRate}
              placeholder={String(profile.data?.settings.taxRate ?? 14)}
              onChange={(e) => setTaxRate(e.target.value)}
            />
            <span className="num mute">{money(totals.taxAmount)}</span>
          </div>
        </div>
        <div className="totals-row totals-final">
          <span>الإجمالي</span>
          <span className="num">{money(totals.total)}</span>
        </div>
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="b-notes">
          ملاحظات
        </label>
        <textarea
          id="b-notes"
          className="textarea"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/* ==========================================================================
   تفاصيل الفاتورة
   ========================================================================== */

function BillDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  const bill = useQuery({
    queryKey: ['bill', id],
    queryFn: () => api<BillRow>(`/finance/bills/${id}`),
  });

  const after = () => {
    void queryClient.invalidateQueries({ queryKey: ['bill', id] });
    onChanged();
  };

  const receive = useMutation({
    mutationFn: () => api(`/finance/bills/${id}/receive`, { method: 'POST' }),
    onSuccess: after,
  });

  const cancel = useMutation({
    mutationFn: () =>
      api(`/finance/bills/${id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      setCancelling(false);
      after();
    },
  });

  const b = bill.data;
  const outstanding = b ? round2(Number(b.total) - Number(b.paidAmount)) : 0;

  return (
    <Modal
      title={b ? `${b.number} — ${b.supplier.name}` : 'فاتورة مورد'}
      onClose={onClose}
      wide
      footer={
        <>
          {b && b.status === 'DRAFT' && (
            <button
              className="btn btn-primary me-auto"
              onClick={() => receive.mutate()}
              disabled={receive.isPending}
            >
              {receive.isPending ? 'جارٍ الاستلام' : 'استلام وترحيل'}
            </button>
          )}
          {b && b.status !== 'DRAFT' && b.status !== 'CANCELLED' && outstanding > 0 && (
            <button className="btn btn-primary me-auto" onClick={() => setPaying(true)}>
              تسجيل سداد
            </button>
          )}
          {b && b.status !== 'CANCELLED' && Number(b.paidAmount) === 0 && (
            <button className="btn btn-danger" onClick={() => setCancelling(true)}>
              إلغاء الفاتورة
            </button>
          )}
          <button className="btn" onClick={onClose}>
            إغلاق
          </button>
        </>
      }
    >
      {bill.isPending && <div className="skeleton" style={{ height: 240 }} />}
      {receive.isError && (
        <div className="alert alert-danger mb-4">
          {receive.error instanceof Error ? receive.error.message : 'تعذّر الاستلام.'}
        </div>
      )}

      {b && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={BILL_STATUS_MAP[b.status].kind}>
                  {BILL_STATUS_MAP[b.status].label}
                </Badge>
              }
              note={b.supplierRef ? `مرجع المورد ${b.supplierRef}` : undefined}
            />
            <Stat label="الإجمالي" value={<span className="num">{money(b.total)}</span>} />
            <Stat label="المسدَّد" value={<span className="num pos">{money(b.paidAmount)}</span>} />
            <Stat
              label="المتبقي"
              value={<span className="num neg">{money(outstanding)}</span>}
              note={`الاستحقاق ${date(b.dueAt)}`}
            />
          </div>

          {b.status === 'DRAFT' && (
            <div className="alert alert-info mb-4">
              الفاتورة مسودة ولم تُرحَّل بعد. الاستلام يثبّتها ويُنشئ قيدها في الدفاتر، وبعده
              لا تُعدَّل.
            </div>
          )}

          <h4 className="section-title">البنود</h4>
          {/* سقفٌ بعدد الصفوف لا بارتفاع الشاشة: الجدول داخل نافذة، ورأسه يلتصق بأعلاها. */}
          <div className="table-wrap sticky-head rows-cap" style={{ ['--rows' as string]: 8 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>الحساب</th>
                  <th>البيان</th>
                  <th style={{ width: 80 }}>الكمية</th>
                  <th style={{ width: 110 }}>السعر</th>
                  <th style={{ width: 120 }}>الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                {b.lines?.map((l) => (
                  <tr key={l.id}>
                    <td className="cell-mute">
                      <span className="num">{l.account.code}</span> {l.account.name}
                    </td>
                    <td>{l.description}</td>
                    <td className="num">{Number(l.quantity)}</td>
                    <td className="num">{money(l.unitPrice)}</td>
                    <td className="num cell-strong">{money(l.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {b.payments && b.payments.length > 0 && (
            <>
              <h4 className="section-title">السداد</h4>
              <div className="table-wrap sticky-head rows-cap" style={{ ['--rows' as string]: 6 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>الرقم</th>
                      <th>التاريخ</th>
                      <th>من</th>
                      <th>الطريقة</th>
                      <th style={{ width: 120 }}>القيمة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.payments.map((p) => (
                      <tr key={p.id}>
                        <td className="num cell-mute">{p.number}</td>
                        <td className="num cell-mute">{date(p.paidAt)}</td>
                        <td className="cell-mute">{p.cashAccount.name}</td>
                        <td className="cell-mute">{p.method ?? '—'}</td>
                        <td className="num cell-strong">{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {b.notes && (
            <>
              <h4 className="section-title">ملاحظات</h4>
              <p className="small">{b.notes}</p>
            </>
          )}
        </>
      )}

      {paying && b && (
        <PaymentModal
          billId={b.id}
          outstanding={outstanding}
          onClose={() => setPaying(false)}
          onSaved={() => {
            setPaying(false);
            after();
          }}
        />
      )}

      {cancelling && (
        <Modal
          title="إلغاء الفاتورة"
          onClose={() => setCancelling(false)}
          footer={
            <>
              <button className="btn" onClick={() => setCancelling(false)}>
                تراجع
              </button>
              <button
                className="btn btn-danger"
                onClick={() => cancel.mutate()}
                disabled={reason.trim().length < 2 || cancel.isPending}
              >
                تأكيد الإلغاء
              </button>
            </>
          }
        >
          <p className="small mb-4">
            القيد لا يُحذف بل يُعكَس بقيد مضاد، فيبقى في الدفتر أثر ما حدث.
          </p>
          <div className="field">
            <label className="label req" htmlFor="cancel-reason">
              سبب الإلغاء
            </label>
            <input
              id="cancel-reason"
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function PaymentModal({
  billId,
  outstanding,
  onClose,
  onSaved,
}: {
  billId: string;
  outstanding: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [cashAccountId, setCashAccountId] = useState('');
  const [amount, setAmount] = useState(String(outstanding));
  const [paidAt, setPaidAt] = useState(today);
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [fxRate, setFxRate] = useState('');

  const cashAccounts = useQuery({
    queryKey: ['cash-accounts', false],
    queryFn: () =>
      api<{ items: Array<Option & { isDefault: boolean; currency?: string }> }>(
        '/finance/cash-accounts',
      ),
  });

  // سداد من خزينة بعملة أجنبية: القيمة بعملتها، والمسدَّد مقابلها بالجنيه.
  const cashCurrency = cashAccounts.data?.items.find((c) => c.id === cashAccountId)?.currency ?? 'EGP';
  const foreign = isForeignCurrency(cashCurrency);
  const rate = Number(fxRate) || 0;

  const pay = useMutation({
    mutationFn: () =>
      api(`/finance/bills/${billId}/payments`, {
        method: 'POST',
        body: {
          cashAccountId,
          amount: foreign ? toBase(Number(amount) || 0, rate) : Number(amount) || 0,
          ...(foreign ? { fxAmount: Number(amount) || 0, fxRate: rate } : {}),
          paidAt,
          method: method || undefined,
          reference: reference || undefined,
        },
      }),
    onSuccess: onSaved,
  });

  return (
    <Modal
      title="تسجيل سداد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => pay.mutate()}
            disabled={
              !cashAccountId || Number(amount) <= 0 || (foreign && rate <= 0) || pay.isPending
            }
          >
            {pay.isPending ? 'جارٍ التسجيل' : 'تسجيل'}
          </button>
        </>
      }
    >
      {pay.isError && (
        <div className="alert alert-danger mb-4">
          {pay.error instanceof Error ? pay.error.message : 'تعذّر تسجيل السداد.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="p-cash">
            من خزينة
          </label>
          <select
            id="p-cash"
            className="select"
            value={cashAccountId}
            onChange={(e) => setCashAccountId(e.target.value)}
          >
            <option value="">اختر</option>
            {cashAccounts.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {isForeignCurrency(c.currency) ? ` (${c.currency})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="p-amount">
            القيمة{foreign ? ` (${currencySymbol(cashCurrency)})` : ''}
          </label>
          <input
            id="p-amount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <span className="tiny mute">
            المتبقي {money(outstanding)}
            {foreign && rate > 0
              ? ` ≈ ${fxMoney(Math.round((outstanding / rate) * 100) / 100, cashCurrency)}`
              : ''}
            .
          </span>
        </div>

        {foreign && (
          <FxRateField
            id="p-rate"
            currency={cashCurrency}
            date={paidAt}
            rate={fxRate}
            onRate={setFxRate}
            fxTotal={Number(amount) || 0}
          />
        )}

        <div className="field">
          <label className="label req" htmlFor="p-date">
            التاريخ
          </label>
          <input
            id="p-date"
            className="input"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="p-method">
            الطريقة
          </label>
          <input
            id="p-method"
            className="input"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            placeholder="تحويل، شيك، نقدًا"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="p-ref">
            المرجع
          </label>
          <input
            id="p-ref"
            className="input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
